import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Plus, Edit, Trash2, Save, X, Search, FolderIcon } from '../components/Icons';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import toast from 'react-hot-toast';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiGet('/api/inventory/categories/');
      if (response.ok) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    }
  };

  const createCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost('/api/inventory/categories/', newCategory);
      if (response.ok) {
        toast.success('Category created successfully!');
        setNewCategory({ name: '', description: '' });
        fetchCategories();
      } else {
        toast.error(response.data.detail || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Error creating category');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiDelete(`/api/inventory/categories/${categoryId}`);
      if (response.ok) {
        toast.success('Category deleted successfully!');
        fetchCategories();
      } else {
        toast.error(response.data.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error deleting category');
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (categoryId, updatedData) => {
    setLoading(true);
    try {
      const response = await apiPut(`/api/inventory/categories/${categoryId}`, updatedData);
      if (response.ok) {
        toast.success('Category updated successfully!');
        setEditingCategory(null);
        fetchCategories();
      } else {
        toast.error(response.data.detail || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Error updating category');
    } finally {
      setLoading(false);
    }
  };

  const filtered = categories.filter(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
                <p className="text-gray-600">Manage product categories</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Category</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category Name</label>
                <input
                  value={newCategory.name}
                  onChange={(e)=>setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Enter category name"
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  value={newCategory.description}
                  onChange={(e)=>setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="Enter category description"
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
              <div className="flex justify-end">
                <button 
                  disabled={loading || !newCategory.name.trim()} 
                  onClick={createCategory} 
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2"/> Create Category
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <Search className="h-4 w-4 text-gray-400 mr-2"/>
              <input value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} placeholder="Search categories..." className="border rounded px-3 py-2 flex-1"/>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-8">
                <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No categories found' : 'No categories yet'}
                </h3>
                <p className="text-gray-500">
                  {searchTerm 
                    ? `No categories match "${searchTerm}"` 
                    : 'Create your first category to get started'
                  }
                </p>
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map(cat => (
                  <li key={cat.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FolderIcon className="h-5 w-5 text-blue-500"/>
                      <div>
                        <span className="text-gray-800 font-medium">{cat.name}</span>
                        {cat.description && (
                          <p className="text-sm text-gray-500">{cat.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingCategory(cat)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Edit category"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteCategory(cat.id, cat.name)}
                        disabled={loading}
                        className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                        title="Delete category"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Edit Category Modal */}
        {editingCategory && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Category</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category Name</label>
                    <input
                      type="text"
                      value={editingCategory.name}
                      onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Category name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editingCategory.description || ''}
                      onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                      className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Category description (optional)"
                      rows="3"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateCategory(editingCategory.id, editingCategory)}
                      disabled={loading || !editingCategory.name.trim()}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Navigation>
  );
};

export default Categories;




